import { useState } from "react";
import { useRecordContext } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import type { Prospect } from "../types";
import { WORKER_URL } from "./enrichFromSource";

type Comp = {
  mlsNumber: string | null;
  price: number | null;
  sqft: number | null;
  beds: number | null;
  baths: number | null;
  address: string;
  soldDate: string | null;
  lat: number | null;
  lng: number | null;
};

type CompsResponse = {
  subject?: { lat: number | null; lng: number | null; sqft: number | null; beds: number | null };
  count?: number;
  radiusMiles?: number;
  monthsBack?: number;
  listings?: Comp[];
  error?: string;
  detail?: string;
};

const usd = (v?: number | null) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(v);

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// Source value the subject marker should sit at — best available estimate.
const subjectValue = (r: Prospect) =>
  r.avm ?? r.tax_value ?? r.list_price ?? r.opening_bid ?? null;

const fullAddress = (r: Prospect) =>
  [r.property_address, r.property_city, r.property_state, r.property_zip]
    .filter(Boolean)
    .join(", ");

export const ProspectComps = () => {
  const record = useRecordContext<Prospect>();
  const [state, setState] = useState<"idle" | "loading" | "error" | "done">("idle");
  const [data, setData] = useState<CompsResponse | null>(null);
  const [errMsg, setErrMsg] = useState<string>("");
  const [hover, setHover] = useState<number | null>(null);

  if (!record) return null;

  const pull = async () => {
    setState("loading");
    setErrMsg("");
    try {
      const res = await fetch(`${WORKER_URL}/comps-by-address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: fullAddress(record),
          zip: record.property_zip,
          beds: record.beds,
          sqft: record.sqft,
        }),
      });
      const json: CompsResponse = await res.json();
      if (!res.ok || json.error) {
        const friendly =
          json.error === "subject_not_found"
            ? "Couldn't locate this property in the MLS data to anchor comps."
            : json.detail || json.error || `Request failed (${res.status})`;
        setErrMsg(friendly);
        setState("error");
        return;
      }
      setData(json);
      setState("done");
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
      setState("error");
    }
  };

  const comps = (data?.listings ?? []).filter(
    (c): c is Comp & { price: number; sqft: number } =>
      c.price != null && c.sqft != null && c.price > 0 && c.sqft > 0,
  );
  const ppsfs = comps.map((c) => c.price / c.sqft);
  const medPpsf = median(ppsfs);
  const medPrice = median(comps.map((c) => c.price));
  const subjSqft = record.sqft ?? data?.subject?.sqft ?? null;
  const subjVal = subjectValue(record);
  const impliedValue = medPpsf && subjSqft ? Math.round(medPpsf * subjSqft) : null;

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between gap-2">
          <div>
            <h6 className="text-sm font-semibold">Quick comps</h6>
            <p className="text-xs text-muted-foreground">
              Sold comps near this property (Repliers MLS), price vs. size.
            </p>
          </div>
          <Button
            variant={state === "done" ? "outline" : "default"}
            size="sm"
            onClick={pull}
            disabled={state === "loading"}
          >
            {state === "loading"
              ? "Pulling…"
              : state === "done"
                ? "Refresh comps"
                : "Pull comps"}
          </Button>
        </div>

        {state === "error" ? (
          <p className="mt-3 text-sm text-destructive">{errMsg}</p>
        ) : null}

        {state === "done" ? (
          comps.length ? (
            <div className="mt-3">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <Stat label="Comps" value={String(comps.length)} />
                <Stat label="Median $/sqft" value={medPpsf ? `$${Math.round(medPpsf)}` : "—"} />
                <Stat label="Median sold" value={usd(medPrice)} />
                <Stat
                  label="Implied value @ subject sqft"
                  value={impliedValue ? usd(impliedValue) : "—"}
                  highlight
                />
              </div>
              <DotPlot
                comps={comps}
                subjSqft={subjSqft}
                subjVal={subjVal}
                medPpsf={medPpsf}
                hover={hover}
                setHover={setHover}
              />
              <HoverDetail comp={hover != null ? comps[hover] : null} />
              <p className="mt-1 text-xs text-muted-foreground">
                {data?.count} closed sales within ~{data?.radiusMiles} mi, last {data?.monthsBack}{" "}
                months
                {subjVal != null
                  ? " · ★ marks this property's est. value"
                  : " · add a valuation to plot this property"}
                .
              </p>
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              No comparable sales found nearby for the current filters.
            </p>
          )
        ) : null}
      </CardContent>
    </Card>
  );
};

const Stat = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) => (
  <div className="flex flex-col">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`font-semibold ${highlight ? "text-emerald-600" : ""}`}>{value}</span>
  </div>
);

const HoverDetail = ({ comp }: { comp: Comp | null }) => (
  <div className="mt-2 h-5 text-xs text-muted-foreground">
    {comp ? (
      <span>
        <span className="font-medium text-foreground">{comp.address}</span> · {usd(comp.price)} ·{" "}
        {comp.sqft?.toLocaleString()} sqft · ${Math.round((comp.price ?? 0) / (comp.sqft || 1))}/sqft
        {comp.beds ? ` · ${comp.beds} bd` : ""}
        {comp.soldDate ? ` · sold ${comp.soldDate.slice(0, 10)}` : ""}
      </span>
    ) : (
      "Hover a dot for sale details"
    )}
  </div>
);

// --- Dependency-free SVG scatter (price vs sqft) -------------------------------
const DotPlot = ({
  comps,
  subjSqft,
  subjVal,
  medPpsf,
  hover,
  setHover,
}: {
  comps: Array<Comp & { price: number; sqft: number }>;
  subjSqft: number | null;
  subjVal: number | null;
  medPpsf: number | null;
  hover: number | null;
  setHover: (i: number | null) => void;
}) => {
  const W = 640;
  const H = 340;
  const padL = 58;
  const padR = 16;
  const padT = 14;
  const padB = 34;

  const hasSubject = subjSqft != null && subjVal != null;
  const xs = comps.map((c) => c.sqft).concat(hasSubject ? [subjSqft as number] : []);
  const ys = comps.map((c) => c.price).concat(hasSubject ? [subjVal as number] : []);
  const xmin = Math.min(...xs) * 0.95;
  const xmax = Math.max(...xs) * 1.05;
  const ymin = Math.min(...ys) * 0.9;
  const ymax = Math.max(...ys) * 1.05;

  const sx = (v: number) => padL + ((v - xmin) / (xmax - xmin || 1)) * (W - padL - padR);
  const sy = (v: number) => H - padB - ((v - ymin) / (ymax - ymin || 1)) * (H - padT - padB);

  const xTicks = ticks(xmin, xmax, 4);
  const yTicks = ticks(ymin, ymax, 4);

  // Median $/sqft trend line endpoints, clamped to the plotted price range.
  const linePts =
    medPpsf != null
      ? [
          [xmin, medPpsf * xmin],
          [xmax, medPpsf * xmax],
        ]
      : null;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mt-3 w-full select-none"
      role="img"
      aria-label="Comparable sales scatter plot"
    >
      {/* axes */}
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#e5e7eb" />
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#e5e7eb" />

      {/* y gridlines + labels ($k) */}
      {yTicks.map((t) => (
        <g key={`y${t}`}>
          <line x1={padL} y1={sy(t)} x2={W - padR} y2={sy(t)} stroke="#f3f4f6" />
          <text x={padL - 6} y={sy(t) + 3} textAnchor="end" fontSize="10" fill="#9ca3af">
            ${Math.round(t / 1000)}k
          </text>
        </g>
      ))}

      {/* x labels (sqft) */}
      {xTicks.map((t) => (
        <text
          key={`x${t}`}
          x={sx(t)}
          y={H - padB + 16}
          textAnchor="middle"
          fontSize="10"
          fill="#9ca3af"
        >
          {Math.round(t).toLocaleString()}
        </text>
      ))}
      <text x={(padL + W - padR) / 2} y={H - 2} textAnchor="middle" fontSize="10" fill="#6b7280">
        Square feet
      </text>

      {/* median $/sqft trend line */}
      {linePts ? (
        <line
          x1={sx(linePts[0][0])}
          y1={sy(linePts[0][1])}
          x2={sx(linePts[1][0])}
          y2={sy(linePts[1][1])}
          stroke="#0FB5BA"
          strokeWidth="1.5"
          strokeDasharray="5 4"
        />
      ) : null}

      {/* comp dots */}
      {comps.map((c, i) => (
        <circle
          key={c.mlsNumber ?? i}
          cx={sx(c.sqft)}
          cy={sy(c.price)}
          r={hover === i ? 6 : 4}
          fill={hover === i ? "#dc2626" : "#2563eb"}
          fillOpacity={hover === i ? 1 : 0.55}
          stroke="#fff"
          strokeWidth="1"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
        />
      ))}

      {/* subject star */}
      {hasSubject ? (
        <g transform={`translate(${sx(subjSqft as number)}, ${sy(subjVal as number)})`}>
          <Star r={9} fill="#f59e0b" />
        </g>
      ) : null}
    </svg>
  );
};

const Star = ({ r, fill }: { r: number; fill: string }) => {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const ang = (Math.PI / 5) * i - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.45;
    pts.push(`${Math.cos(ang) * rad},${Math.sin(ang) * rad}`);
  }
  return <polygon points={pts.join(" ")} fill={fill} stroke="#fff" strokeWidth="1" />;
};

// Evenly spaced ticks across [min, max].
function ticks(min: number, max: number, n: number): number[] {
  if (!isFinite(min) || !isFinite(max) || max <= min) return [];
  const step = (max - min) / n;
  return Array.from({ length: n + 1 }, (_, i) => min + step * i);
}
